import API_ENDPOINTS from "@/common/api/apiEndpoints";

/**
 * Result data source selector.
 *
 * The automation results UI (web/src/pages/test-result + test-run/run-results) is
 * data-source-agnostic. It renders whatever the result endpoints return. To show
 * AUTOMATED test-cycle runs in that exact UI we keep the components/hooks identical
 * and only swap the endpoint base via this `source` flag:
 *
 *   'plan'  -> legacy test_plan_* result endpoints (default — Test Plans unchanged)
 *   'cycle' -> cloned cycle read-engine at /cycle_results/* (returns identical shapes)
 *
 * The cloned cycle endpoints accept the SAME query param names (testPlanId,
 * testPlanResultId, parentId, iteration, ...) so callers don't change their queries.
 */
export type ResultSource = "plan" | "cycle";

interface ResultEndpoints {
  planResults: string;
  caseResults: string;
  suiteResults: string;
  deviceResults: string;
  stepResults: string;
  dataDrivenResults: string;
}

const PLAN_ENDPOINTS: ResultEndpoints = {
  planResults: API_ENDPOINTS.TESTRESULTS.testPlanResults,
  caseResults: API_ENDPOINTS.TESTRESULTS.testCaseResults,
  suiteResults: API_ENDPOINTS.TESTRESULTS.testSuiteResults,
  deviceResults: API_ENDPOINTS.TESTRESULTS.testDeviceResults,
  stepResults: API_ENDPOINTS.TESTRESULTS.testStepResults,
  dataDrivenResults: API_ENDPOINTS.TESTCASE.testDataDrivenResults,
};

const CYCLE_ENDPOINTS: ResultEndpoints = {
  planResults: "cycle_results/test_plan_results",
  caseResults: "cycle_results/test_case_results",
  suiteResults: "cycle_results/test_suite_results",
  deviceResults: "cycle_results/test_device_results",
  stepResults: "cycle_results/test_step_results",
  dataDrivenResults: "cycle_results/test_data_driven_results",
};

export const resultEndpoints = (
  source: ResultSource = "plan",
): ResultEndpoints => (source === "cycle" ? CYCLE_ENDPOINTS : PLAN_ENDPOINTS);
